import React, { Fragment } from 'react';
import {
	Button,
	Dropdown,
	Form,
	Modal,
	Table,
	Segment,
	Dimmer,
	Loader,
	Image,
	Accordion,
	Icon
} from 'semantic-ui-react';
import { inject, observer } from 'mobx-react';
import Rx from 'stores/Rx';
import * as styles from './styles.js';
import { observable, IObservableArray, toJS } from 'mobx';
import type { AssignableUser } from 'stores/Account';
import MessageBox from 'components/MessageBox';
import MsgStore from 'stores/MsgStore';
import UiShared from 'stores/UiShared';
import Auth from 'stores/Auth';
import API from 'api';
import _ from 'lodash';
import eyeMap from 'data/eyeMap';
import DayPickerInput from 'components/DayPickerInput';

@inject(rootStore => ({
	auth: rootStore.auth,
	uiShared: rootStore.uiShared
}))
@observer
export default class ScriptApproval extends React.Component {
	props: {
		isOpen: boolean,
		onRequestReload: () => {},
		onRequestClose: () => {},
		auth?: Auth,
		uiShared: UiShared,
		canSubmit: boolean
	};

	static defaultProps = {
		isOpen: false
	};

	@observable rx: Rx = new Rx();
	msgStore: MsgStore = new MsgStore();

	@observable active = {};
	@observable responseData = '';
	@observable assessmentDetailsText = '';
	@observable param: string = '';
	@observable showDebug: boolean = false;
	@observable showConfirmDlg: boolean = false;
	@observable requiredValid: boolean = false;
	@observable answersSubmitted: boolean = false;
	@observable isConfirmed: boolean = false;
	@observable debugIdx: number = 1;
	@observable showLoader: boolean = true;
	@observable saving: boolean = false;
	@observable inputParamsValue: string = '';
	@observable inputJsonValue: string = '';
	@observable questions: IObservableArray<object> = [];
	@observable restrictionCode: number = 0;
	@observable expandedRows = [];
	@observable dataRestrictions = [];
	@observable requiredQuestions = [];
	@observable responses: IObservableArray<object> = [];
	@observable
	commands = [
		{
			name: 'createAuthority',
			json: true
		},
		{
			name: 'updateAuthority',
			json: true
		},
		{
			name: 'getRestrictionQuestion',
			params: 'prescriberID/itemCode/restrictionCode'
		}
	];

	setActive = val => {
		this.inputJsonValue = '';
		this.inputParamsValue = '';
		this.responseData = '';
		this.active = val;
	};

	validate = () => {
		this.requiredValid = !this.requiredQuestions.some(q => q.restrictionQuestionAnswer === '');
	};

	componentDidUpdate = async prevProps => {
		const { uiShared, quietSubmit } = this.props;
		try {
			if (this.props.scriptId && prevProps.scriptId !== this.props.scriptId) {
				this.rx = new Rx();
				if (quietSubmit) {
					uiShared.showSpinner('Submitting...');
				} else {
					this.showLoader = true;
				}
				this.msgStore.clear();
				this.restrictionCode = 0;
				this.questions = [];
				this.requiredQuestions = [];
				this.dataRestrictions = [];
				this.expandedRows = [];
				this.inputParamsValue = '';
				this.responses = {};
				this.isConfirmed = false;
				this.showDebug = false;
				this.assessmentDetailsText = '';
				this.answersSubmitted = false;
				this.requiredValid = false;

				this.rx = await Rx.loadById(this.props.scriptId);

				if (!quietSubmit) {
					this.dataRestrictions = await API.get(
						`/ItemInfors?filter=${JSON.stringify({
							where: {
								and: [
									{ disabled: false },
									{ itemCode: this.rx.itemCode },
									{ indication: this.rx.indication },
									{ treatment: this.rx.ItemInfo.treatment }
								]
							}
						})}`
					);
					if (this.dataRestrictions && this.dataRestrictions.length === 1) {
						this.restrictionCode = this.dataRestrictions[0].restrictionCode;
					}
				} else {
					this.restrictionCode = this.rx.ItemInfo?.restrictionCode;
				}

				if (this.rx.questions) {
					this.questions = JSON.parse(this.rx.questions);
					this.showLoader = false;
					this.requiredValid = true;
					if (quietSubmit === true) {
						this.isConfirmed = true;
						await this.createAuthority();
					}
				} else {
					await this.getRestrictionQuestion();
				}
			}
		} catch (e) {
			this.msgStore.setError(e);
		}
	};

	getRestrictionQuestion = async () => {
		try {
			this.msgStore.clear();
			let match;
			this.showLoader = true;
			const rx = this.rx;
			const prescriberNo = toJS(rx).PrescriberPractice.Prescriber.prescriberNo;
			const code = this.restrictionCode;
			const cmd = this.commands.find(c => c.name === 'getRestrictionQuestion');
			this.setActive(cmd);
			this.inputParamsValue = `${prescriberNo.trim()}/${rx.itemCode?.trim()}/${code}`;
			await this.call();
			if (this.rx.eye) {
				match = eyeMap[this.rx.eye.toLowerCase()];
			}
			if (this.responseData.restrictionQuestionDetails?.length > 0) {
				this.questions = this.responseData.restrictionQuestionDetails.map(q => {
					let qVal;
					if (q.restrictionAnswerList?.restrictionAnswer) {
						const options = toJS(q.restrictionAnswerList.restrictionAnswer);
						const restrictionQuestionText = q.restrictionQuestionText?.toLowerCase();
						const shouldMatch =
							restrictionQuestionText &&
							(restrictionQuestionText.indexOf('eye') !== -1 ||
								restrictionQuestionText.indexOf('choroidal neovascularisation') !== -1);
						if (match && options && shouldMatch) {
							const found = _.find(options, el => {
								return el.restrictionAnswerText?.toLowerCase() == match;
							});
							if (found) {
								qVal = found.restrictionAnswerId;
							}
						}
					}
					const val = {
						restrictionQuestionOrderNumber: q.restrictionQuestionOrderNumber,
						restrictionQuestionText: q.restrictionQuestionText,
						restrictionQuestionCode: q.restrictionQuestionCode,
						restrictionAnswerType: q.restrictionAnswerType,
						restrictionAnswerList: q.restrictionAnswerList,
						restrictionQuestionMandatory: q.restrictionQuestionMandatory,
						restrictionQuestionAnswer: qVal || ''
					};
					return val;
				});
				this.requiredQuestions = this.questions.filter(
					q => q.restrictionQuestionMandatory === true
				);
			}
			this.validate();
		} catch (e) {
			this.msgStore.setError(e);
		} finally {
			this.showLoader = false;
		}
	};

	createAuthority = async () => {
		const { uiShared } = this.props;
		const { isConfirmed } = this;
		try {
			this.msgStore.clear();
			if (!isConfirmed) {
				this.showConfirmDlg = true;
				return;
			}
			uiShared.showSpinner('Submitting...');
			const { rx, questions, restrictionCode } = this;
			const overridable = rx.authorityUniqueID; // const overridable = this.rx.overridable; //temporary allow all statuses to call an UPDATE
			const cmdName = overridable ? 'updateAuthority' : 'createAuthority';
			const cmd = this.commands.find(c => c.name === cmdName);
			this.setActive(cmd);
			const { patientMedicare, patientName, patientSurname } = this.rx.Patient;
			const prescriberNo = toJS(rx).PrescriberPractice.Prescriber.prescriberNo;
			const data = {
				authorityPrescriptionNumber: rx.authorityNo,
				prescriberId: prescriberNo,
				certifyIndicator: 'Y',
				patientDetails: {
					medicareNumber: patientMedicare,
					firstName: patientName,
					surname: patientSurname
				},
				itemDetails: {
					code: rx.itemCode.trim(),
					quantity: rx.quantity,
					numberOfRepeats: rx.noOfRepeats,
					dose: rx.dose?.toString()
				}
			};
			if (rx.PrescriberPractice?.Practice?.practiceType?.toLowerCase() === 'hospital') {
				data.hospitalDetails = {
					providerNumber: rx.PrescriberPractice.Practice.practiceProviderNo
				};
			}
			data.restrictionQuestionDetails = { code: restrictionCode?.toString() };
			if (questions?.length > 0) {
				const questionsFiltered = questions.filter(
					q => !(!q.restrictionQuestionMandatory && q.restrictionQuestionAnswer === '')
				);
				data.restrictionQuestionDetails.questions = questionsFiltered.map(q => {
					const isList = q.restrictionAnswerType?.trim() === 'LIST';
					let ret = {};
					if (isList) {
						ret = {
							code: q.restrictionQuestionCode,
							answerListCode: q.restrictionAnswerList?.restrictionAnswerListCode,
							answerId: Number(q.restrictionQuestionAnswer)
						};
					} else {
						ret = {
							code: q.restrictionQuestionCode,
							answerText: q.restrictionQuestionAnswer
						};
					}
					return ret;
				});
			}
			if (overridable) {
				//temporary allow all statuses to call an UPDATE // const overridable = this.rx.overridable; => const overridable = rx.authorityUniqueID;
				data.overrideCode = '1';
				data.authorityUniqueId = rx.authorityUniqueID;
			}
			this.inputJsonValue = JSON.stringify(data);
			this.answersSubmitted = true;
			await this.call();

			if (this.responseData.assessmentDetails) {
				const resultText = this.responseData.assessmentDetails?.text;
				const result = resultText?.toLowerCase();
				const isResolved = result?.includes('approved') || result?.includes('rejected');
				if (isResolved) {
					this.debugIdx = -1;
					this.assessmentDetailsText = resultText;
					await this.saveCreateAuthorityResponse();
				}
			} else {
				this.showDebug = true;
			}
		} catch (e) {
			this.msgStore.setError(e);
		} finally {
			uiShared.hideModal();
			this.isConfirmed = false;
		}
	};

	call = async () => {
		const { inputParamsValue, inputJsonValue } = this;
		const { name, json } = this.active;
		console.log(' inputParamsValue, inputJsonValue', inputParamsValue, inputJsonValue);
		console.log(' name, json', name, json);
		const body = json ? inputJsonValue : { inputParams: encodeURI(inputParamsValue.trim()) };
		const ext = { rxId: this.rx?.id };
		let req = {
			name,
			body,
			ext
		};
		let response = await Rx.callProda(req);
		this.responseData = toJS(response);
		if (this.responseData.redirectProda) {
			this.props.uiShared.redirectProda = true;
		}
		console.log('responseData', JSON.stringify(this.responseData));
		if (name === 'getRestrictionQuestion' && this.responseData.statusMessages?.length > 0) {
			let noQuestions = false;
			_.forEach(this.responseData.statusMessages, m => {
				if (m.reasonText?.includes('not have any associated questions')) {
					noQuestions = true;
					return false;
				}
			});
			if (noQuestions) {
				this.msgStore.setInfo('Item does not have any associated questions.');
			}
		} else if (this.responseData.statusMessages?.length > 0 || this.responseData.code) {
			this.showDebug = true;
		}
	};

	saveCreateAuthorityResponse = async () => {
		const { uiShared, onRequestReload } = this.props;
		const { rx, responseData } = this;
		let reasonCode = '';
		let newLine = '';
		try {
			this.msgStore.clear();
			const { assessmentDetails } = responseData;
			const status = assessmentDetails?.text.trim().toLowerCase();
			let normalized = '';
			if (['approved', 'rejected'].includes(status)) {
				normalized = status;
			} else if (status === 'previously rejected/now approved') {
				normalized = 'approved';
			}
			if (normalized) {
				rx.status = normalized;
			}
			if (normalized === 'approved') {
				rx.approvalNumber = responseData.authorityApprovalNumber;
				let warning = '';
				let reasonType = '';
				responseData.statusMessages?.forEach((m, idx) => {
					newLine =
						responseData.statusMessages.length > 1 && idx !== responseData.statusMessages.length - 1
							? '\r\n'
							: '';
					reasonType = m.reasonType?.toLowerCase();
					if (m.reasonText && ['warning', 'information'].includes(reasonType)) {
						warning += `${m.reasonText}${newLine}`;
						reasonCode += m.reasonCode ? `${m.reasonCode}, ` : '';
					}
				});
				if (warning) {
					rx.warning = warning;
					rx.reasonCode = reasonCode;
				} else {
					//approved without warning, clear for a case when updated and re-approved after approved with warning
					if (rx.warning) {
						rx.warning = '';
						rx.reasonCode = '';
					}
				}
			} else if (status === 'rejected') {
				let reason = '';
				responseData.statusMessages?.forEach((m, idx) => {
					newLine =
						responseData.statusMessages.length > 1 && idx !== responseData.statusMessages.length - 1
							? '\r\n'
							: '';
					reason += m.reasonText ? `${m.reasonText}${newLine}` : '';
					reasonCode += m.reasonCode ? `${m.reasonCode}, ` : '';
				});
				const nonOverridable = _.find(responseData.statusMessages, { overrideIndicator: 'N' });
				if (!nonOverridable) {
					rx.overridable = true;
				}
				rx.reasonText = reason;
				rx.reasonCode = reasonCode;
			}
			rx.authorityUniqueID = responseData.authorityUniqueId;
			rx.finalisationDate = new Date();
			await rx.update();
			await Promise.all([rx.reload(), onRequestReload()]);
		} catch (e) {
			this.msgStore.setError(e);
		} finally {
			uiShared.hideModal();
		}
	};

	renderList = (question, key) => {
		const options = question?.restrictionAnswerList.restrictionAnswer.map((item, i) => {
			return {
				key: i,
				text: item.restrictionAnswerText,
				value: item.restrictionAnswerId
			};
		});

		return (
			<Form.Field
				key={key}
				width={16}
				className={styles.questionCont}
				required={question.restrictionQuestionMandatory ? true : false}
			>
				<label className={styles.questionLbl}>{question.restrictionQuestionText}</label>
				<Dropdown
					selection
					options={options}
					placeholder=""
					value={question.restrictionQuestionAnswer || ''}
					onChange={(e, data) => {
						question.restrictionQuestionAnswer = data.value;
						this.validate();
					}}
					width={8}
				/>
			</Form.Field>
		);
	};

	renderInput = (question, key) => {
		return (
			<Form.Field
				key={key}
				required={question.restrictionQuestionMandatory ? true : false}
				className={styles.questionCont}
			>
				<label className={styles.questionLbl}>{question.restrictionQuestionText}</label>
				<Form.Input
					value={question.restrictionQuestionAnswer || ''}
					onChange={(e, data) => {
						question.restrictionQuestionAnswer = data.value;
						this.validate();
					}}
					width={16}
				/>
			</Form.Field>
		);
	};

	renderDate = (question, key) => {
		let value = question.restrictionQuestionAnswer;
		if (value?.length === 8) {
			value = `${value.slice(4)}-${value.slice(2, 4)}-${value.slice(0, 2)}`;
		}

		return (
			<Form.Field
				width={16}
				key={key}
				required={question.restrictionQuestionMandatory ? true : false}
				className={styles.questionCont}
			>
				<label className={styles.questionLbl}>{question.restrictionQuestionText}</label>
				<DayPickerInput
					width={4}
					value={value?.toDate() || ''}
					onDayChange={day => {
						const date = day
							.getDate()
							.toString()
							.padStart(2, '0');
						const month = (day.getMonth() + 1).toString().padStart(2, '0');
						const year = day.getFullYear().toString();
						question.restrictionQuestionAnswer = `${date}${month}${year}`;
						this.validate();
					}}
				/>
			</Form.Field>
		);
	};

	handleRowClick = rowId => {
		const currentExpandedRows = this.expandedRows;
		const isRowCurrentlyExpanded = currentExpandedRows.includes(rowId);

		const newExpandedRows = isRowCurrentlyExpanded
			? currentExpandedRows.filter(id => id !== rowId)
			: currentExpandedRows.concat(rowId);

		this.expandedRows = newExpandedRows;
	};

	renderItem = item => {
		const clickCallback = () => this.handleRowClick(item.restrictionCode);
		const itemRows = [];
		if (!this.expandedRows.includes(item.restrictionCode)) {
			const txtMore = item.restrictionText?.length > 170;
			itemRows.push(
				<Table.Row key={'row-data-' + item.restrictionCode}>
					<Table.Cell
						verticalAlign="top"
						onClick={() => this.getRestrictionQuestion(item.restrictionCode)}
					>
						<a className={styles.code}>{item.restrictionCode}</a>
					</Table.Cell>
					<Table.Cell onClick={clickCallback} className={styles.hand}>
						{item.restrictionText.slice(0, 170)}
						{txtMore && (
							<Fragment>
								{'...'}
								<br />
								<a>Display Restriction Text</a>
							</Fragment>
						)}
					</Table.Cell>
				</Table.Row>
			);
		} else {
			itemRows.push(
				<Table.Row key={'row-expanded-' + item.restrictionCode}>
					<Table.Cell
						verticalAlign="top"
						onClick={() => this.getRestrictionQuestion(item.restrictionCode)}
					>
						<a className={styles.code}>{item.restrictionCode}</a>
					</Table.Cell>
					<Table.Cell onClick={clickCallback} className={styles.hand}>
						{item.restrictionText}
					</Table.Cell>
				</Table.Row>
			);
		}

		return itemRows;
	};

	renderRestrictions = () => {
		let allItemRows = [];
		this.dataRestrictions?.forEach(item => {
			const perItemRows = this.renderItem(item);
			allItemRows = allItemRows.concat(perItemRows);
		});
		return (
			this.dataRestrictions?.length > 0 && (
				<Table striped>
					<Table.Header>
						<Table.Row>
							<Table.HeaderCell width={2} className={styles.header}>
								Restriction
								<br />
								Code
							</Table.HeaderCell>
							<Table.HeaderCell width={14} className={styles.header}>
								<br />
								Restriction Text
							</Table.HeaderCell>
						</Table.Row>
					</Table.Header>
					<Table.Body>{allItemRows}</Table.Body>
				</Table>
			)
		);
	};

	renderQuestions = () => {
		const renderedQuestions = this.questions.map((question, i) => {
			let ctrl;
			switch (question.restrictionAnswerType?.trim()) {
				case 'LIST':
					ctrl = this.renderList(question, i);
					break;
				case 'DATE':
					ctrl = this.renderDate(question, i);
					break;
				default:
					ctrl = this.renderInput(question, i);
			}
			return ctrl;
		});
		return <div className={styles.questionsCont}>{renderedQuestions}</div>;
	};

	handleDebugClick = (e, titleProps) => {
		const { index } = titleProps;
		const { debugIdx } = this;
		const newIndex = debugIdx === index ? -1 : index;
		this.debugIdx = newIndex;
	};

	renderDebug = () => {
		const { responseData, debugIdx } = this;
		return (
			<Accordion style={{ marginTop: '30px' }}>
				<Accordion.Title active={debugIdx === 0} index={0} onClick={this.handleDebugClick}>
					<Icon name="dropdown" />
					DETAILS
				</Accordion.Title>
				<Accordion.Content active={debugIdx === 0}>
					<Segment>{responseData && this.prettyPrintJson(responseData)}</Segment>
				</Accordion.Content>
			</Accordion>
		);
	};

	prettyPrintJson = data => (
		<div>
			<pre className={styles.pre}>{JSON.stringify(data, null, 2)}</pre>
		</div>
	);

	renderResult = () => {
		const { assessmentDetailsText, responseData } = this;
		if (!assessmentDetailsText) return;
		const rejectedReason =
			assessmentDetailsText === 'Rejected' &&
			responseData.statusMessages?.map((m, i) => {
				return <p>{m.reasonText}</p>;
			});
		const isApproved = assessmentDetailsText.toLowerCase().includes('approved');
		const warning =
			isApproved &&
			responseData.statusMessages?.map((m, i) => {
				return (
					<p>
						{i === 0 ? 'Warning: ' : ''}
						{m.reasonText}
					</p>
				);
			});
		return (
			<div>
				<h3>{`Script has been ${assessmentDetailsText}`}</h3>
				{isApproved && (
					<Fragment>
						<div>
							<Form.Input
								required
								label="Approval Number"
								value={responseData.authorityApprovalNumber}
								width={16}
							/>
						</div>
						{warning && <div className={styles.resultReasonCnt}>{warning}</div>}
					</Fragment>
				)}
				{assessmentDetailsText === 'Rejected' && (
					<div className={styles.resultReasonCnt}>
						<h4>Reason(s) for Rejection:</h4>
						{rejectedReason}
					</div>
				)}
			</div>
		);
	};

	renderBtns = () => {
		const { onRequestClose, canSubmit } = this.props;
		const { requiredValid, assessmentDetailsText, questions } = this;
		return (
			<Fragment>
				<Button onClick={onRequestClose} floated="right">
					Close
				</Button>
				{!assessmentDetailsText && (
					<Button
						primary
						onClick={() => this.saveQuestions()}
						floated="right"
						disabled={requiredValid && questions ? false : true}
					>
						Save
					</Button>
				)}
				{!assessmentDetailsText && canSubmit && (
					<Button
						floated="right"
						primary
						className={styles.greenButton}
						onClick={() => this.createAuthority()}
						disabled={requiredValid && questions ? false : true}
					>
						Request Authority
					</Button>
				)}
			</Fragment>
		);
	};

	saveQuestions = async () => {
		const { onRequestReload, onRequestClose } = this.props;
		const { rx, questions } = this;
		this.saving = true;
		if (questions) {
			rx.questions = JSON.stringify(questions);
		}
		rx.status = 'pending';
		await rx.update();
		onRequestReload();
		this.saving = false;
		onRequestClose();
	};

	render() {
		const { isOpen, onRequestClose } = this.props;
		const {
			rx,
			questions,
			restrictionCode,
			renderRestrictions,
			renderQuestions,
			showDebug,
			renderDebug,
			renderResult,
			answersSubmitted,
			assessmentDetailsText,
			dataRestrictions,
			saving
		} = this;

		return (
			<Fragment>
				<Modal
					open={isOpen}
					size="small"
					onClose={onRequestClose}
					className={styles.modalApprovalCont}
					closeOnDimmerClick={false}
				>
					<Fragment>
						<Dimmer active={saving} inverted>
							<Loader content="Saving" />
						</Dimmer>
						<Modal.Header>
							{rx.searchPrescriber} - {rx.searchPatientName} - {rx.authorityNo}
						</Modal.Header>
						<Modal.Content>
							<Form>
								<div>
									<div className={styles.infoBox}>
										<span className={styles.boxLb}>Drug:</span>
										<span>{rx?.drug}</span>
										<br />
										<span className={styles.boxLb}>Indication:</span>
										<span>{rx?.indication}</span>
										<br />
										<span className={styles.boxLb}>Eye:</span>
										<span>{rx?.eye}</span>
									</div>
								</div>
								<div className={styles.buttonsRow}>{this.renderBtns()}</div>
								<MessageBox msgStore={this.msgStore} />

								<div className={styles.approvalCont}>
									{this.showLoader ? (
										<Fragment>
											<Segment>
												<Dimmer active inverted>
													<Loader inverted>Loading</Loader>
												</Dimmer>

												<Image src="https://react.semantic-ui.com/images/wireframe/short-paragraph.png" />
											</Segment>
										</Fragment>
									) : (
										<Fragment>
											{!restrictionCode && dataRestrictions.length > 1 && renderRestrictions()}
											{questions?.length > 0 && !answersSubmitted && renderQuestions()}
											{assessmentDetailsText && answersSubmitted && renderResult()}
											{showDebug && renderDebug()}
										</Fragment>
									)}
								</div>
								<div className={styles.buttonsRow} style={{ marginBottom: '35px' }}>
									{this.renderBtns()}
								</div>
							</Form>
						</Modal.Content>
					</Fragment>
				</Modal>
				<Modal
					size="mini"
					open={this.showConfirmDlg}
					onClose={() => (this.showConfirmDlg = false)}
					className={styles.modalConfirmation}
					closeIcon={true}
				>
					<Modal.Content>
						<p>
							I agree all information for this PBS authority application is true and correct. I
							acknowledge that the date on the prescription will become the submission date.
						</p>
					</Modal.Content>
					<Modal.Actions>
						<Button
							positive
							icon="checkmark"
							labelPosition="right"
							content="I agree"
							onClick={() => {
								this.showConfirmDlg = false;
								this.isConfirmed = true;
								this.createAuthority();
							}}
						/>
					</Modal.Actions>
				</Modal>
			</Fragment>
		);
	}
}



// WEBPACK FOOTER //
// ./src/components/Script/ScriptApproval.js